import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Icon from './AppIcon';
import Button from './ui/Button';
import { sendNewsletterSubscriptionEmail } from '../utils/emailService';
import { scrollToElement } from '../utils/scrollUtils';
import { useContactInfo, getWhatsAppUrl } from '../utils/contactObfuscation';
import { primarySocialLinks } from '../data/socialLinks';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const contactInfo = useContactInfo();

  // Handle smooth scrolling to sections
  const handleSectionClick = async (e, sectionId) => {
    e.preventDefault();
    
    // If we're on the homepage, just scroll to the section
    if (location.pathname === '/') {
      const success = await scrollToElement(sectionId, { 
        smooth: true,
        maxRetries: 15,
        retryDelay: 150
      });
      if (success) {
        // Update URL hash without triggering scroll
        window.history.pushState(null, '', `#${sectionId}`);
      }
    } else {
      // Navigate to homepage with hash
      navigate(`/#${sectionId}`);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await sendNewsletterSubscriptionEmail(email);

      if (result.success) {
        setSubmitMessage('Thank you for subscribing to our newsletter!');
        setEmail('');
      } else {
        setSubmitMessage('Sorry, there was an error. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Sorry, there was an error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppClick = () => {
    const message = "Hi! I'm interested in your web development services. Can you help me get started?";
    const whatsappUrl = contactInfo.whatsapp || getWhatsAppUrl(message);
    window.open(whatsappUrl, '_blank');
  };

  const handleEmailClick = () => {
    if (!contactInfo.mailtoLink) return;
    const subject = "Web Development Services Inquiry";
    const body = "Hi Octalchip team,\n\nI'm interested in your web development services. Could you please provide more information about your pricing and features?\n\nBest regards,\n[Your Name]";
    const mailtoUrl = `${contactInfo.mailtoLink}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl);
  };

  return (
    <footer className="bg-surface border-t border-border mt-16">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 sm:px-6 py-10 sm:py-12 lg:py-16 max-w-7xl">
        {/* Top Section: Company Info, Contact Info, and Newsletter */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 mb-10 lg:mb-12 pb-8 lg:pb-10 border-b border-border">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-4">
              <img 
                src="/octalchiplogo.svg" 
                alt="OctalChip - Software Development Company Logo - Web, Mobile, AI/ML Services" 
                className="h-8 sm:h-10 lg:h-12 w-auto object-contain"
              />
            </div>
            <p className="text-sm sm:text-base text-text-secondary leading-relaxed max-w-lg">
              Empowering businesses with cutting-edge web, mobile, AI, and cloud solutions for a digital future. We transform ideas into innovative digital experiences that drive growth and success.
            </p>
          </div>

          {/* Contact Info */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="font-semibold text-text-primary text-base sm:text-lg text-center">Contact Us</h4>
            <div className="flex justify-center">
              <div className="space-y-3 sm:space-y-3.5">
                <div className="flex items-center space-x-3">
                  <Icon name="Phone" size={18} className="text-primary flex-shrink-0 w-5" />
                  {contactInfo.phoneLink && (
                    <a 
                      href={contactInfo.phoneLink} 
                      className="text-sm text-text-secondary hover:text-primary transition-colors"
                    >
                      {contactInfo.phone}
                    </a>
                  )}
                </div>
                
                {contactInfo.mailtoLink && (
                  <div className="flex items-center space-x-3">
                    <Icon name="Mail" size={18} className="text-primary flex-shrink-0 w-5" />
                    <a 
                      href={contactInfo.mailtoLink} 
                      className="text-sm text-text-secondary hover:text-primary transition-colors break-all"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                )}
                
                <div className="flex items-center space-x-3">
                  <Icon name="Clock" size={18} className="text-primary flex-shrink-0 w-5" />
                  <span className="text-sm text-text-secondary">Mon - Fri, 9AM - 6PM IST</span>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Icon name="MapPin" size={18} className="text-primary flex-shrink-0 w-5 mt-0.5" />
                  <div className="text-sm text-text-secondary">
                    <p className="font-medium text-text-primary mb-1">Head Office</p>
                    <p>Octalchip, 404, Opera point,</p>
                    <p>Kothariya, Gujarat, India, 360022</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Newsletter Subscription */}
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-text-primary text-lg sm:text-xl mb-2">Stay Updated</h4>
              <p className="text-sm text-text-secondary mb-4">
                Subscribe to our newsletter for the latest updates, tech insights, and exclusive offers.
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="px-4 py-3 border border-border rounded-lg text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary flex-1 bg-white"
                required
              />
              <Button
                type="submit"
                variant="outline"
                size="md"
                animation="glow"
                disabled={isSubmitting}
                className="border-primary text-primary bg-white hover:bg-primary hover:text-white whitespace-nowrap"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
            {submitMessage && (
              <p className="text-sm text-center sm:text-left text-text-secondary">
                {submitMessage}
              </p>
            )}
          </div>
        </div>

        {/* Main Navigation Grid */}
        <div className="mb-10 lg:mb-12">
          {/* Navigation Columns Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 sm:gap-8">
          {/* Company Links */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="font-semibold text-text-primary text-base sm:text-lg">Company</h4>
            <ul className="space-y-2 sm:space-y-2.5">
              <li>
                <Link to="/about" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">About Us</span>
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Contact</span>
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">All Services</span>
                </Link>
              </li>
              <li>
                <a 
                  href="/#why-choose-us" 
                  onClick={(e) => handleSectionClick(e, 'why-choose-us')}
                  className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group"
                >
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Why Choose Us</span>
                </a>
              </li>
              <li>
                <Link to="/product" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Products</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="font-semibold text-text-primary text-base sm:text-lg">Services</h4>
            <ul className="space-y-2 sm:space-y-2.5">
              <li>
                <Link to="/services/web-development" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Web Development</span>
                </Link>
              </li>
              <li>
                <Link to="/services/mobile-app-development" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Mobile Apps</span>
                </Link>
              </li>
              <li>
                <Link to="/services/backend-development" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Backend Development</span>
                </Link>
              </li>
              <li>
                <Link to="/services/ui-ux-design" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">UI/UX Design</span>
                </Link>
              </li>
              <li>
                <Link to="/services/cloud-devops" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Cloud & DevOps</span>
                </Link>
              </li>
              <li>
                <Link to="/services/ai-integration" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">AI Integration</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* AI/ML Services */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="font-semibold text-text-primary text-base sm:text-lg">AI/ML Services</h4>
            <ul className="space-y-2 sm:space-y-2.5">
              <li>
                <Link to="/ai-ml-services" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">All AI/ML Services</span>
                </Link>
              </li>
              <li>
                <Link to="/services/ai-ml-services/machine-learning" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Machine Learning</span>
                </Link>
              </li>
              <li>
                <Link to="/services/ai-ml-services/natural-language-processing" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">NLP</span>
                </Link>
              </li>
              <li>
                <Link to="/services/ai-ml-services/computer-vision" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Computer Vision</span>
                </Link>
              </li>
              <li>
                <Link to="/services/ai-ml-services/ai-chatbots" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">AI Chatbots</span>
                </Link>
              </li>
              <li>
                <Link to="/services/ai-ml-services/data-science" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Data Science</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Automation & Resources */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="font-semibold text-text-primary text-base sm:text-lg">Automation</h4>
            <ul className="space-y-2 sm:space-y-2.5">
              <li>
                <Link to="/services/workflow-automation" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Workflow Automation</span>
                </Link>
              </li>
              <li>
                <Link to="/services/n8n-automation" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">n8n Automation</span>
                </Link>
              </li>
              <li>
                <Link to="/services/zapier-integration" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Zapier Integration</span>
                </Link>
              </li>
              <li>
                <Link to="/services/make-automation" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Make Automation</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal & Support */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="font-semibold text-text-primary text-base sm:text-lg">Legal</h4>
            <ul className="space-y-2 sm:space-y-2.5">
              <li>
                <Link to="/privacy" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Privacy Policy</span>
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Terms of Service</span>
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Support</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/landing#get-quote" 
                  className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group"
                >
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Get Quote</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="font-semibold text-text-primary text-base sm:text-lg">Resources</h4>
            <ul className="space-y-2 sm:space-y-2.5">
              <li>
                <Link to="/blog" className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group">
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Blog</span>
                </Link>
              </li>
              <li>
                <a 
                  href="/#skills" 
                  onClick={(e) => handleSectionClick(e, 'skills')}
                  className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group"
                >
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Technologies</span>
                </a>
              </li>
              <li>
                <a 
                  href="/#industries" 
                  onClick={(e) => handleSectionClick(e, 'industries')}
                  className="text-sm text-text-secondary hover:text-primary transition-colors flex items-center group"
                >
                  <Icon name="ChevronRight" size={14} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">Industries</span>
                </a>
              </li>
            </ul>
          </div>
          </div>
        </div>

        {/* CTA Buttons Section */}
        <div className="pt-6 sm:pt-8 border-t border-border">
          {/* Social Media Icons - At Top of CTA Section */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 md:gap-4 lg:gap-4 mb-6 sm:mb-8 max-w-full">
            {primarySocialLinks.map((social, index) => (
              <a 
                key={index}
                href={social.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 lg:w-12 lg:h-12 rounded-full text-text-secondary hover:text-primary hover:bg-primary/10 active:bg-primary/20 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/50 flex-shrink-0"
                aria-label={social.name}
              >
                <Icon name={social.icon} size={18} className="sm:w-5 sm:h-5 md:w-5 md:h-5 lg:w-6 lg:h-6" />
              </a>
            ))}
          </div>
          
          {/* Separator Line */}
          <div className="flex justify-center my-6 sm:my-8">
            <div className="w-full max-w-md h-px bg-border"></div>
          </div>
          
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-center justify-center">
            <Button
              variant="outline"
              size="md"
              iconName="MessageCircle"
              animation="bounce"
              className="border-primary text-primary bg-white hover:bg-primary hover:text-white"
              onClick={handleWhatsAppClick}
            >
              WhatsApp Us
            </Button>
            
            <Button
              variant="outline"
              size="md"
              iconName="Mail"
              animation="pulse"
              className="border-primary text-primary bg-white hover:bg-primary hover:text-white"
              onClick={handleEmailClick}
            >
              Email Us
            </Button>
            
            <Button
              variant="outline"
              size="md"
              iconName="Phone"
              className="border-primary text-primary bg-white hover:bg-primary hover:text-white"
              onClick={() => contactInfo.phoneLink && window.open(contactInfo.phoneLink)}
            >
              Call Us
            </Button>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="bg-primary text-white py-4 sm:py-5">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm sm:text-base text-center sm:text-left">
              Copyright © {new Date().getFullYear()} Octalchip. All rights reserved.
            </p>
            <div className="flex items-center gap-2 text-sm sm:text-base">
              <span>Powered by</span>
              <a 
                href="https://www.octalchip.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:underline font-medium"
              >
                Octalchip
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 